import React from 'react';
import { BMSVoucherGuest } from 'services/ItineraryManagerApi/types';
import { Table } from 'ui/Table';
import { ESortOrder } from 'store/common/types';
import { ExpandableTable } from 'ui/ExpandableTable/ExpandableTable';
import { SlideToggle } from 'ui/SlideToggle';
import { formatDateDisplay } from 'utils';

interface IVoucherGuestsEditTableProps {
  guests: BMSVoucherGuest[];
  onToggleVisibility: (guestUuid: string, isVisible: boolean) => void;
}

export const VoucherGuestsEditTable: React.FC<IVoucherGuestsEditTableProps> = ({ guests, onToggleVisibility }) => {
  if (!guests || guests.length === 0) return null;

  // Format time to display properly
  const formatTime = (time?: string) => {
    if (!time) return '-';
    return time;
  };

  // Format arrival/departure for expandable table
  const formatArrivalDeparture = (date?: string, time?: string, flightNumber?: string) => {
    const hasDate = !!date;
    const hasTime = !!time;
    const hasFlight = !!flightNumber;

    if (!hasDate && !hasFlight) return '-';

    let result = '';

    // Add date and time if available
    if (hasDate) {
      result = formatDateDisplay(date);
      if (hasTime) {
        result += `, ${formatTime(time)}`;
      }
    }

    // Add flight number if available
    if (hasFlight) {
      if (result) {
        result += '   ';
      }
      result += `Flight N. ${flightNumber}`;
    }

    return result;
  };

  // Format guest name
  const formatGuestName = (guest: BMSVoucherGuest) => {
    const parts = [guest.title, guest.firstName, guest.lastName].filter(Boolean);
    return parts.join(' ') || '-';
  };

  // Calculate guest age based on dob or fallback to manualAge
  const calculateGuestAge = (guest: BMSVoucherGuest) => {
    if (guest.dob) {
      // Calculate age from date of birth using current date
      const today = new Date();
      const birthDate = new Date(guest.dob);
      const age = Math.floor((today.getTime() - birthDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
      return age.toString();
    }
    if (guest.manualAge) {
      return guest.manualAge.toString();
    }
    return '-';
  };

  // Render visibility toggle
  const renderVisibilityToggle = (guest: BMSVoucherGuest) => (
    <SlideToggle
      id={`toggle-${guest.uuid}`}
      checked={guest.isVisible}
      onActivate={() => onToggleVisibility(guest.uuid, true)}
      onDeactivate={() => onToggleVisibility(guest.uuid, false)}
      labelClassName={!guest.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
      buttonClassName={!guest.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
    />
  );

  return (
    <>
      {/* Desktop table view */}
      <div className="table-wrapper hidden md:block">
        <Table
          columns={[
            { id: 'name', label: `Guests (${guests.length})`, width: 'auto' },
            { id: 'dob', label: 'D.O.B', width: 'auto' },
            { id: 'age', label: 'Age', width: 'auto' },
            { id: 'arrival', label: 'Arrival', width: 'auto' },
            { id: 'arrivalFlight', label: 'Flight N.', width: 'auto' },
            { id: 'departure', label: 'Departure', width: 'auto' },
            { id: 'departureFlight', label: 'Flight N.', width: 'auto' },
            { id: 'visible', label: 'Visible', width: '100px' },
          ]}
          messageWhenEmpty="No guest information available"
          sortBy=""
          sortOrder={ESortOrder.ASC}
          onSort={() => {}}
          rows={guests.map(guest => ({
            id: guest.uuid,
            cells: [
              { id: 'name', value: formatGuestName(guest), contentClassName: !guest.isVisible ? 'text-gray-100' : '' },
              {
                id: 'dob',
                value: guest.dob ? formatDateDisplay(guest.dob) : '-',
                contentClassName: !guest.isVisible ? 'text-gray-100' : '',
              },
              { id: 'age', value: calculateGuestAge(guest), contentClassName: !guest.isVisible ? 'text-gray-100' : '' },
              {
                id: 'arrival',
                value: guest.arrival_date
                  ? `${formatDateDisplay(guest.arrival_date)} ${formatTime(guest.arrival_time)}`
                  : '-',
                contentClassName: !guest.isVisible ? 'text-gray-100' : '',
              },
              {
                id: 'arrivalFlight',
                value: guest.arrival_flightNumber || '-',
                contentClassName: !guest.isVisible ? 'text-gray-100' : '',
              },
              {
                id: 'departure',
                value: guest.departure_date
                  ? `${formatDateDisplay(guest.departure_date)} ${formatTime(guest.departure_time)}`
                  : '-',
                contentClassName: !guest.isVisible ? 'text-gray-100' : '',
              },
              {
                id: 'departureFlight',
                value: guest.departure_flightNumber || '-',
                contentClassName: !guest.isVisible ? 'text-gray-100' : '',
              },
              {
                id: 'visible',
                value: renderVisibilityToggle(guest),
              },
            ],
          }))}
        />
      </div>

      {/* Mobile expandable table view */}
      <div className="expandable-table-wrapper block md:hidden w-full">
        <ExpandableTable
          data={guests.map(guest => ({
            id: guest.uuid,
            title: formatGuestName(guest),
            titleClassName: !guest.isVisible ? 'text-gray-100' : '',
            row: {
              id: guest.uuid,
              cells: [
                {
                  title: 'Name',
                  id: 'name',
                  value: formatGuestName(guest),
                  className: !guest.isVisible ? 'text-gray-100' : '',
                },
                {
                  title: 'D.O.B',
                  id: 'dob',
                  value: guest.dob ? formatDateDisplay(guest.dob) : '-',
                  className: !guest.isVisible ? 'text-gray-100' : '',
                },
                {
                  title: 'Age',
                  id: 'age',
                  value: calculateGuestAge(guest),
                  className: !guest.isVisible ? 'text-gray-100' : '',
                },
                {
                  title: 'Arrival',
                  id: 'arrival',
                  value: formatArrivalDeparture(guest.arrival_date, guest.arrival_time, guest.arrival_flightNumber),
                  className: !guest.isVisible ? 'text-gray-100' : '',
                },
                {
                  title: 'Departure',
                  id: 'departure',
                  value: formatArrivalDeparture(
                    guest.departure_date,
                    guest.departure_time,
                    guest.departure_flightNumber
                  ),
                  className: !guest.isVisible ? 'text-gray-100' : '',
                },
                {
                  title: 'Visible',
                  id: 'visible',
                  value: (
                    <div className="flex items-center">
                      <span className="mr-2">{guest.isVisible ? 'Yes' : 'No'}</span>
                      {renderVisibilityToggle(guest)}
                    </div>
                  ),
                },
              ],
            },
          }))}
        />
      </div>
    </>
  );
};
